import dayjs from 'dayjs';
import { lazy, useMemo, useRef, useState } from 'react';
import * as React from 'react';
import { CSSTransition } from 'react-transition-group';

import TextButton from '@/components/elements/Button/TextButton';
import { MatchResultInfo } from '@/components/layouts/Modal/MatchDetail';
import RacerNameData from '@/components/layouts/Race/RaceCard/RacerNameData';
import useModal from '@/hooks/modal/useModal';
import {
  EntryRacer,
  EntryRacerNote,
  EntryRacerScore,
  EntryRacerScoreDetailOwn,
  MultiraceType,
  ReleaseStatus,
} from '@/lib/apollo/types/gen/graphql';
import { RaceInfoType, ScoreCsvIndex } from '@/modelTypes';
import { forecastMarkSelector } from '@/selectors/forecastMark';
import { defaultTheme } from '@/utility/theme';
import { toHalfWidth } from '@/utility/utils';

import ModalBase from '../../Modal/Base';
import BracketNumberData from './BracketNumberData';
import OverlayTable from './OverlayTable';
import HighlightValue from './Partials/HighlightValue';
import TableData from './Partials/TableData';
import TableHeader from './Partials/TableHeader';
import PercentageOfBetsData from './PercentageOfBetsData';
import RecentResultData from './RecentResultData';
import {
  ArrivalOrderData,
  BicycleNumberData,
  BorderData,
  BorderHeader,
  Container,
  GearRatioBefore,
  MarkSymbol,
  RacerNameHeader,
  ScrollWrapper,
  Table,
  TableRow,
  WinningAvarageHeader,
} from './style';

const MatchDetail = lazy(
  () => import('@/components/layouts/Modal/MatchDetail'),
);

interface RaceCardProps {
  heldDate: dayjs.Dayjs;
  isFirstDay: boolean;
  entryRacers: EntryRacer[];
  entryRacerNoteList?: EntryRacerNote[];
  showEntryRacerNoteModal?: (entryRacer: EntryRacer) => void;
  raceInfoType: RaceInfoType;
  multiraceType?: MultiraceType;
  scrollable?: boolean;
  scoreDetailOwns?: EntryRacerScoreDetailOwn[];
  scoreDetailMatch?: EntryRacerScoreDetailOwn[];
}

export type MatchRecordHeader = {
  registrationNum: number;
  shortName: string;
};

export type MatchRecordResult = {
  registrationNum: number;
  totalWinCnt: number;
  totalLoseCnt: number;
  matchResult: { [key: number]: string };
  matchResultDetail: { [key: number]: MatchResultInfo[] };
};

export enum ScoreCsvEnum {
  ScoreCsv1 = 'scoreCsv1',
  ScoreCsv2 = 'scoreCsv2',
  ScoreCsv3 = 'scoreCsv3',
  ScoreCsv4 = 'scoreCsv4',
  ScoreCsv5 = 'scoreCsv5',
  ScoreCsv6 = 'scoreCsv6',
  ScoreCsv7 = 'scoreCsv7',
}

const RaceCard: React.FC<RaceCardProps> = ({
  heldDate,
  isFirstDay,
  entryRacers,
  entryRacerNoteList,
  showEntryRacerNoteModal,
  raceInfoType,
  multiraceType,
  scrollable,
  scoreDetailOwns,
  scoreDetailMatch,
}) => {
  const baseRef = useRef<HTMLTableElement | null>(null);
  const targetRef = useRef<HTMLTableHeaderCellElement | null>(null);

  const [isActive, setIsActive] = useState<boolean>(false);
  const [openMatchDetail, setOpenMatchDetail] = useModal(false);

  const convertToNumArray = (key: keyof EntryRacerScore): number[] =>
    entryRacers.map(er => Number(er.entryRacerScore[key]));

  const isResult = useMemo(
    () => raceInfoType === RaceInfoType.Result,
    [raceInfoType],
  );
  const isScore = useMemo(
    () => raceInfoType === RaceInfoType.Score,
    [raceInfoType],
  );
  const isVelodromeResult = useMemo(
    () => raceInfoType === RaceInfoType.VelodromeResult,
    [raceInfoType],
  );

  const isRecentResult = useMemo(
    () => raceInfoType === RaceInfoType.RecentResult,
    [raceInfoType],
  );
  const isNone = useMemo(
    () => raceInfoType === RaceInfoType.None,
    [raceInfoType],
  );

  const onScroll = () => {
    if (targetRef.current && baseRef.current) {
      setIsActive(
        baseRef.current.scrollLeft > targetRef.current.scrollLeft + 90,
      );
    }
  };

  const filterScoreDetailOwns = (registrationNum: number) => {
    if (scoreDetailOwns) {
      return scoreDetailOwns.filter(
        item => item.registrationNum == registrationNum,
      );
    }
    return [];
  };

  const filterScoreDetailMatch = (registrationNum: number) => {
    if (scoreDetailMatch) {
      return scoreDetailMatch.filter(
        item => item.registrationNum == registrationNum,
      );
    }
    return [];
  };

  const matchRecordHeader = useMemo(() => {
    const result: MatchRecordHeader[] = [];

    if (entryRacers) {
      entryRacers.forEach((er: EntryRacer) => {
        const tmp: MatchRecordHeader = {
          registrationNum: er.registrationNum,
          shortName: er.shortName,
        };
        result.push(tmp);
      });
    }
    return result;
  }, [entryRacers]);

  const getMatchRank = (scoreCsvA: string, scoreCsvB: string) => {
    if (scoreCsvA == '' || scoreCsvB == '') {
      return [0, 0];
    }

    // 直接対決が無ければ「0,0」を返却（CSVの4番目「レース番号」の一致有無を確認）
    const csvSplitA = scoreCsvA.split(',');
    const csvSplitB = scoreCsvB.split(',');
    if (csvSplitA[ScoreCsvIndex.RaceNum] !== csvSplitB[ScoreCsvIndex.RaceNum]) {
      return [0, 0];
    }

    // 日付が表示している開催日付以降の場合は対象外
    if (csvSplitA[ScoreCsvIndex.HeldDate] >= heldDate.format('YYYY-MM-DD')) {
      return [0, 0];
    }

    // 直接対決があれば「Aの順位, Bの順位」を返却（着順：CSVの3番目）
    const aRank = Number(csvSplitA[ScoreCsvIndex.Rank]);
    const bRank = Number(csvSplitB[ScoreCsvIndex.Rank]);

    // 「0位」同士で、ステータスも空文字同士なら「レース中止」判定なので集計しない
    if (aRank === 0 && bRank === 0) {
      if (
        csvSplitA[ScoreCsvIndex.PersonalStatus] == '' &&
        csvSplitB[ScoreCsvIndex.PersonalStatus] == ''
      ) {
        return [0, 0];
      }
    }

    // どちらかが「0位」で、「欠車」の場合は集計しない（厳密には直接対決していないので）
    if (aRank === 0 && csvSplitA[ScoreCsvIndex.PersonalStatus] == '欠') {
      return [0, 0];
    }

    if (bRank === 0 && csvSplitB[ScoreCsvIndex.PersonalStatus] == '欠') {
      return [0, 0];
    }

    // 「0位」は落車や失格等で順位がつかなかった場合なので「99位」として返却
    return [aRank == 0 ? 99 : aRank, bRank == 0 ? 99 : bRank];
  };

  const setResultInfo = (
    scoreDetail: EntryRacerScoreDetailOwn,
    argRaceDate: string,
    argRaceNum: string,
    argRaceEventName: string,
    argRankMine: number,
    argStatusMine: string,
    argRankOpponent: number,
    argStatusOpponent: string,
  ) => {
    const resultInfoBase: MatchResultInfo = {
      raceDate: argRaceDate,
      velodromeCode: scoreDetail.velodromeCode,
      grade: scoreDetail.gradeName,
      raceNum: argRaceNum,
      raceClassName: argRaceEventName,
      rankMine: argRankMine,
      statusMine: argStatusMine,
      rankOpponent: argRankOpponent,
      statusOpponent: argStatusOpponent,
    };
    return resultInfoBase;
  };

  const calcScoreCsv = (
    scoreDetailA: EntryRacerScoreDetailOwn,
    scoreDetailB: EntryRacerScoreDetailOwn,
    matchDetailA: MatchResultInfo[],
    matchDetailB: MatchResultInfo[],
  ) => {
    var countWinA = 0;
    var countWinB = 0;
    var countDraw = 0;

    // 詳細表示のとき、新しい順で表示するので「７日目 -> １日目」の順で集計する
    const targetList = [
      ScoreCsvEnum.ScoreCsv7,
      ScoreCsvEnum.ScoreCsv6,
      ScoreCsvEnum.ScoreCsv5,
      ScoreCsvEnum.ScoreCsv4,
      ScoreCsvEnum.ScoreCsv3,
      ScoreCsvEnum.ScoreCsv2,
      ScoreCsvEnum.ScoreCsv1,
    ];

    targetList.forEach((target: ScoreCsvEnum) => {
      var result = getMatchRank(scoreDetailA[target], scoreDetailB[target]);
      if (result[0] !== 0 && result[1] !== 0) {
        matchDetailA.push(
          setResultInfo(
            scoreDetailA,
            scoreDetailA[target].split(',')[ScoreCsvIndex.HeldDate],
            scoreDetailA[target].split(',')[ScoreCsvIndex.RaceNum],
            scoreDetailA[target].split(',')[ScoreCsvIndex.RaceEventName],
            result[0],
            scoreDetailA[target].split(',')[ScoreCsvIndex.PersonalStatus],
            result[1],
            scoreDetailB[target].split(',')[ScoreCsvIndex.PersonalStatus],
          ),
        );

        matchDetailB.push(
          setResultInfo(
            scoreDetailB,
            scoreDetailB[target].split(',')[ScoreCsvIndex.HeldDate],
            scoreDetailB[target].split(',')[ScoreCsvIndex.RaceNum],
            scoreDetailB[target].split(',')[ScoreCsvIndex.RaceEventName],
            result[1],
            scoreDetailB[target].split(',')[ScoreCsvIndex.PersonalStatus],
            result[0],
            scoreDetailA[target].split(',')[ScoreCsvIndex.PersonalStatus],
          ),
        );

        if (result[0] == result[1]) {
          countDraw++;
        } else if (result[0] < result[1]) {
          countWinA++;
        } else if (result[0] > result[1]) {
          countWinB++;
        }
      }
    });

    return [countWinA, countWinB, countDraw];
  };

  const getWinCount = (
    scoreListA: EntryRacerScoreDetailOwn[],
    matchTarget: number,
    matchDetailA: MatchResultInfo[],
    matchDetailB: MatchResultInfo[],
  ) => {
    const scoreListB = filterScoreDetailMatch(matchTarget);
    var winCnt = 0;
    var loseCnt = 0;
    var drawCnt = 0;

    scoreListA.forEach((si: EntryRacerScoreDetailOwn) => {
      const sameRace = scoreListB.filter(
        item =>
          item.heldFirstDate.toString() === si.heldFirstDate.toString() &&
          item.velodromeCode === si.velodromeCode,
      ); // 同一開催初日 + 同一競輪場 のデータを取得

      if (sameRace.length == 1) {
        // CSV内の比較処理
        const matchResult = calcScoreCsv(
          si,
          sameRace[0],
          matchDetailA,
          matchDetailB,
        );
        winCnt = winCnt + matchResult[0];
        loseCnt = loseCnt + matchResult[1];
        drawCnt = drawCnt + matchResult[2];
      }
    });

    return [winCnt, loseCnt, drawCnt];
  };

  const setMatchRecordResult = (input: MatchRecordResult[]) => {
    for (var i = 0; i < entryRacers.length; i++) {
      const targetNumI = entryRacers[i].registrationNum;
      const scoreListI = filterScoreDetailMatch(entryRacers[i].registrationNum);

      for (var j = i; j < entryRacers.length; j++) {
        if (i === j) {
          continue; // [i==j] -> 同じ選手同士の比較なので処理スキップ
        }

        const matchDetailI: MatchResultInfo[] = [];
        const matchDetailJ: MatchResultInfo[] = [];
        const targetNumJ = entryRacers[j].registrationNum;
        const res = getWinCount(
          scoreListI,
          targetNumJ,
          matchDetailI,
          matchDetailJ,
        );

        if (res[0] !== 0 || res[1] !== 0 || res[2] !== 0) {
          input.filter(Item => {
            if (Item.registrationNum === targetNumI) {
              Item.matchResult[targetNumJ] = `${res[0]}-${res[1]}`;
              Item.matchResultDetail[targetNumJ] = matchDetailI.concat();
              Item.totalWinCnt = Item.totalWinCnt + res[0];
              Item.totalLoseCnt = Item.totalLoseCnt + res[1];
            }

            if (Item.registrationNum === targetNumJ) {
              Item.matchResult[targetNumI] = `${res[1]}-${res[0]}`;
              Item.matchResultDetail[targetNumI] = matchDetailJ.concat();
              Item.totalWinCnt = Item.totalWinCnt + res[1];
              Item.totalLoseCnt = Item.totalLoseCnt + res[0];
            }
          });
        }
      }
    }
  };

  const matchRecordItems = useMemo(() => {
    const result: MatchRecordResult[] = [];

    if (entryRacers) {
      entryRacers.forEach((er: EntryRacer) => {
        const tmp: MatchRecordResult = {
          registrationNum: er.registrationNum,
          totalWinCnt: 0,
          totalLoseCnt: 0,
          matchResult: {},
          matchResultDetail: {},
        };
        tmp.matchResult[er.registrationNum] = '-';
        result.push(tmp);
      });

      setMatchRecordResult(result);
    }

    return result;
  }, [scoreDetailMatch, entryRacers]);

  const getTotalResult = (regNum: number) => {
    var res = ' - ';
    if (matchRecordItems) {
      matchRecordItems.filter(item => {
        if (item.registrationNum === regNum) {
          res = `${item.totalWinCnt}-${item.totalLoseCnt}`;
        }
      });
    }
    return res;
  };

  const getMatchResult = (regNumA: number, regNumB: number) => {
    if (matchRecordItems) {
      const matchResult = matchRecordItems.filter(
        item => item.registrationNum === regNumA,
      )[0].matchResult[regNumB];

      return <span style={{ textDecoration: 'underline' }}>{matchResult}</span>;
    }
    return <></>;
  };

  const isMissedRacer = (er: EntryRacer) =>
    er.releaseStatus !== ReleaseStatus.Released;

  const makeRecentResultHeader = (index: number) => {
    return (
      <TableHeader
        style={{
          width: '200px',
          textAlign: 'left',
          paddingLeft: defaultTheme.spacing.s,
        }}
      >
        {isFirstDay
          ? `直近${index + 1}`
          : index === 0
          ? `今回`
          : `直近${index}`}
      </TableHeader>
    );
  };

  const makeRecentResultHeaders = () => {
    const DISPLAY_COUNT = Number(import.meta.env.VITE_RACER_SCORE_DETAIL_COUNT); // 直近成績表示数（ENVから取得）
    const results = [];
    for (let i = 0; i < DISPLAY_COUNT; i++) {
      results.push(makeRecentResultHeader(i));
    }
    return results;
  };
  const makeVelodromeResultHeaders = () => {
    const DISPLAY_COUNT2 = Number(
      import.meta.env.VITE_RACER_SCORE_DETAIL_COUNT_VELODROME,
    ); // 当場成績表示数（ENVから取得）
    const results = [];
    for (let i = 0; i < DISPLAY_COUNT2; i++) {
      results.push(makeRecentResultHeader(i));
    }
    return results;
  };

  const [mineName, setMineName] = useState<string>('');
  const [opponentName, setOpponentName] = useState<string>('');
  const [matchResults, setMatchResults] = useState<MatchResultInfo[]>([]);
  const [highlightTargets, setHighlightTargets] = useState<number[]>([]);
  const [isModeRaceResult, setIsModeRaceResult] = useState<boolean>(false);
  const setMatchDetail = (regNumA: number, regNumB: number) => {
    if (matchRecordHeader && matchRecordItems) {
      matchRecordHeader.forEach((er: MatchRecordHeader) => {
        if (er.registrationNum == regNumA) {
          setMineName(er.shortName);
        }
        if (er.registrationNum == regNumB) {
          setOpponentName(er.shortName);
        }
      });

      setMatchResults(
        matchRecordItems
          .filter(item => item.registrationNum === regNumA)[0]
          .matchResultDetail[regNumB].concat(),
      );
      setHighlightTargets([regNumA, regNumB]);
      setIsModeRaceResult(false);
      setOpenMatchDetail(true);
    }
  };

  const openRaceResultModal = (
    matchResult: MatchResultInfo,
    highlightTarget: number,
  ) => {
    setMineName('');
    setOpponentName('');

    const tmpHighlightTargets: number[] = [];
    tmpHighlightTargets.push(highlightTarget);
    setHighlightTargets(tmpHighlightTargets);

    const tmpMatchResult: MatchResultInfo[] = [];
    tmpMatchResult.push(matchResult);
    setMatchResults(tmpMatchResult);

    setIsModeRaceResult(true);
    setOpenMatchDetail(true);
  };

  return (
    <Container>
      <CSSTransition in={!!scrollable && isActive} timeout={100} unmountOnExit>
        <OverlayTable
          entryRacers={entryRacers}
          entryRacerNoteList={entryRacerNoteList}
          showEntryRacerNoteModal={showEntryRacerNoteModal}
        />
      </CSSTransition>
      <ScrollWrapper scrollable={scrollable} ref={baseRef} onScroll={onScroll}>
        <Table>
          <thead>
            <tr>
              {isResult ? (
                <BorderHeader style={{ width: '20px' }} isBorderLeftNone>
                  着
                </BorderHeader>
              ) : (
                <>
                  {!isNone && (
                    <BorderHeader isBorderLeftNone style={{ width: '33px' }}>
                      予想
                    </BorderHeader>
                  )}
                  <BorderHeader style={{ width: '20px' }}>枠</BorderHeader>
                </>
              )}
              <BorderHeader style={{ width: '20px' }} ref={targetRef}>
                車
              </BorderHeader>
              <RacerNameHeader isBorderRightNone={isNone} isNone={isNone}>
                選手名
              </RacerNameHeader>
              {!isNone && (
                <>
                  {isScore && (
                    <>
                      <TableHeader style={{ width: '33px' }}>期別</TableHeader>
                      <TableHeader style={{ width: '33px' }}>級班</TableHeader>
                      <BorderHeader
                        style={{ width: '60px' }}
                        isBorderRightNone={isScore}
                      >
                        競走得点
                      </BorderHeader>
                    </>
                  )}
                  {!(
                    isRecentResult ||
                    isResult ||
                    isScore ||
                    isVelodromeResult
                  ) && (
                    <>
                      <TableHeader style={{ width: '22px' }}>S</TableHeader>
                      <TableHeader style={{ width: '22px' }}>B</TableHeader>
                      <TableHeader style={{ width: '22px' }}>逃</TableHeader>
                      <TableHeader style={{ width: '22px' }}>捲</TableHeader>
                      <TableHeader style={{ width: '22px' }}>差</TableHeader>
                      <BorderHeader style={{ width: '22px' }}>マ</BorderHeader>

                      <TableHeader style={{ width: '51px' }}>勝率</TableHeader>
                      <TableHeader style={{ width: '51px' }}>
                        連対率
                      </TableHeader>
                      <BorderHeader style={{ width: '60px' }}>
                        3連対率
                      </BorderHeader>

                      <TableHeader style={{ width: '33px' }}>1着</TableHeader>
                      <TableHeader style={{ width: '33px' }}>2着</TableHeader>
                      <TableHeader style={{ width: '33px' }}>3着</TableHeader>
                      <BorderHeader style={{ width: '39px' }}>
                        着外
                      </BorderHeader>

                      <TableHeader style={{ width: '40px' }}>脚</TableHeader>
                      <BorderHeader style={{ width: '40px' }}>
                        ギア
                      </BorderHeader>
                      {matchRecordHeader && entryRacers && (
                        <>
                          <TableHeader style={{ width: '60px' }}>
                            対戦成績
                          </TableHeader>
                          {matchRecordHeader.map((item, idx) => (
                            <TableHeader style={{ width: '51px' }} key={idx}>
                              {item.shortName}
                            </TableHeader>
                          ))}
                        </>
                      )}
                    </>
                  )}
                </>
              )}
              {isRecentResult && <>{makeRecentResultHeaders()}</>}
              {isVelodromeResult && <>{makeVelodromeResultHeaders()}</>}
              {multiraceType && !isRecentResult && !isVelodromeResult && (
                <WinningAvarageHeader isBorderRightNone>
                  状況/支持率
                </WinningAvarageHeader>
              )}
              {isResult && (
                <>
                  <TableHeader style={{ width: '33px' }}>着差</TableHeader>
                  <TableHeader style={{ width: '33px' }}>上り</TableHeader>
                  <TableHeader style={{ width: '33px' }}>決</TableHeader>
                  <BorderHeader
                    style={{ width: '33px' }}
                    isBorderRightNone={isResult}
                  >
                    B
                  </BorderHeader>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {entryRacers &&
              entryRacers.map((er, eri) => (
                <TableRow
                  key={eri}
                  bicycleNum={isResult ? 0 : er.bicycleNum}
                  disabled={isMissedRacer(er)}
                >
                  {isResult ? (
                    <ArrivalOrderData arrivalOrder={er.arrivalOrder!}>
                      {isMissedRacer(er)
                        ? '欠'
                        : er.arrivalOrder || er.playerStatus}
                    </ArrivalOrderData>
                  ) : (
                    <>
                      {!isNone && (
                        <BorderData
                          isBorderLeftNone
                          style={{
                            backgroundColor: isMissedRacer(er)
                              ? defaultTheme.background.darkGray
                              : 'inherit',
                            fontFamily: 'sans-serif',
                          }}
                        >
                          <MarkSymbol
                            path={
                              forecastMarkSelector(er.forecastMark).markSymbol
                            }
                          />
                        </BorderData>
                      )}
                      <BracketNumberData
                        bracketNum={er.bracketNum}
                        entryRacers={entryRacers}
                        index={eri}
                        isRecentResult={isRecentResult}
                        isVelodromeResult={isVelodromeResult}
                      />
                    </>
                  )}
                  <BicycleNumberData
                    bicycleNum={er.bicycleNum}
                    disabled={isMissedRacer(er)}
                  >
                    {er.bicycleNum}
                  </BicycleNumberData>
                  <RacerNameData
                    entryRacer={er}
                    raceInfoType={raceInfoType}
                    averageRaceScoreNumArray={convertToNumArray(
                      'averageRaceScore',
                    )}
                    entryRacerNoteList={entryRacerNoteList}
                    showEntryRacerNoteModal={showEntryRacerNoteModal}
                  />
                  {!isNone && (
                    <>
                      {isScore && (
                        <>
                          <TableData>{er.graduated}</TableData>
                          <TableData>
                            {toHalfWidth(`${er.gradeName}${er.unitName}`)}
                          </TableData>
                          <BorderData isBorderRightNone={isScore}>
                            <HighlightValue
                              numArray={convertToNumArray('averageRaceScore')}
                              fractionDigits={2}
                            >
                              {er.entryRacerScore.averageRaceScore}
                            </HighlightValue>
                          </BorderData>
                        </>
                      )}
                      {!(
                        isRecentResult ||
                        isResult ||
                        isScore ||
                        isVelodromeResult
                      ) && (
                        <>
                          <TableData>
                            {er.entryRacerScore.standingCount}
                          </TableData>
                          <TableData>
                            <HighlightValue
                              numArray={convertToNumArray('backCount')}
                            >
                              {er.entryRacerScore.backCount}
                            </HighlightValue>
                          </TableData>
                          <TableData>
                            {er.entryRacerScore.pacesetterCount}
                          </TableData>
                          <TableData>
                            {er.entryRacerScore.comeFromBehindCount}
                          </TableData>
                          <TableData>
                            {er.entryRacerScore.lookerCount}
                          </TableData>
                          <BorderData>
                            {er.entryRacerScore.markCount}
                          </BorderData>

                          <TableData>
                            <HighlightValue
                              numArray={convertToNumArray('winningAverage')}
                              fractionDigits={1}
                            >
                              {er.entryRacerScore.winningAverage}
                            </HighlightValue>
                          </TableData>
                          <TableData>
                            <HighlightValue
                              numArray={convertToNumArray('top2Ratio')}
                              fractionDigits={1}
                            >
                              {er.entryRacerScore.top2Ratio}
                            </HighlightValue>
                          </TableData>
                          <BorderData>
                            <HighlightValue
                              numArray={convertToNumArray('top3Ratio')}
                              fractionDigits={1}
                            >
                              {er.entryRacerScore.top3Ratio}
                            </HighlightValue>
                          </BorderData>

                          <TableData>{er.entryRacerScore.topCount}</TableData>
                          <TableData>{er.entryRacerScore.rank2Count}</TableData>
                          <TableData>{er.entryRacerScore.rank3Count}</TableData>
                          <BorderData>
                            {er.entryRacerScore.unplacedCount}
                          </BorderData>

                          <TableData>{er.racingStyle}</TableData>
                          <BorderData>
                            {er.gearRatioBefore && (
                              <GearRatioBefore>
                                {er.gearRatioBefore}
                              </GearRatioBefore>
                            )}
                            {er.gearRatio}
                          </BorderData>

                          <TableData>
                            {getTotalResult(er.registrationNum)}
                          </TableData>
                          {entryRacers.map((er2, idx) => (
                            <TableData key={idx}>
                              {er.registrationNum === er2.registrationNum ? (
                                <>-</>
                              ) : (
                                <TextButton
                                  size="s"
                                  type="button"
                                  onClick={() => {
                                    setMatchDetail(
                                      er.registrationNum,
                                      er2.registrationNum,
                                    );
                                  }}
                                >
                                  <span>
                                    {getMatchResult(
                                      er.registrationNum,
                                      er2.registrationNum,
                                    )}
                                  </span>
                                </TextButton>
                              )}
                            </TableData>
                          ))}
                        </>
                      )}
                    </>
                  )}
                  {isRecentResult && er.entryRacerScore.entryRacerScoreDetails && (
                    <>
                      <RecentResultData
                        scoreDetailOwns={filterScoreDetailOwns(
                          er.registrationNum,
                        )}
                        viewHeldDate={heldDate}
                        isFirstDay={isFirstDay}
                        isVelodromeResult={isVelodromeResult}
                        openRaceResultModal={openRaceResultModal}
                      />
                    </>
                  )}
                  {isVelodromeResult &&
                    er.entryRacerScore.entryRacerScoreDetails && (
                      <>
                        <RecentResultData
                          scoreDetailOwns={filterScoreDetailOwns(
                            er.registrationNum,
                          )}
                          viewHeldDate={heldDate}
                          isFirstDay={isFirstDay}
                          isVelodromeResult={isVelodromeResult}
                          openRaceResultModal={openRaceResultModal}
                        />
                      </>
                    )}
                  {multiraceType && !isRecentResult && !isVelodromeResult && (
                    <PercentageOfBetsData
                      bicycleNum={er.bicycleNum}
                      multiracePercentageOfBets={er.multiracePercentageOfBets}
                      singleRacePercentageOfBets={er.singleRacePercentageOfBets}
                      multiraceType={multiraceType}
                    />
                  )}
                  {isResult && (
                    <>
                      <TableData>{er.marginInfo}</TableData>
                      <TableData>
                        {er.arrivalOrder! > 0 &&
                          Boolean(er.finalStagePlayerTime) &&
                          er.finalStagePlayerTime!.toFixed(1)}
                      </TableData>
                      <TableData>{er.decidingFactor}</TableData>
                      <BorderData isBorderRightNone={isResult}>
                        <>
                          {er.isHomeGet && 'H'}
                          {er.isBackGet && 'B'}
                        </>
                      </BorderData>
                    </>
                  )}
                </TableRow>
              ))}
          </tbody>
        </Table>
      </ScrollWrapper>
      <ModalBase
        isOpen={openMatchDetail}
        handleClose={() => setOpenMatchDetail(false)}
        desktopWidth="840px"
        mobileWidth="343px"
        desktopHeight="626px"
        mobileHeight="648px"
        backgroundColor={defaultTheme.background.white}
      >
        <MatchDetail
          mineName={mineName}
          opponentName={opponentName}
          matchResults={matchResults}
          highlightTargets={highlightTargets}
          isModeRaceResult={isModeRaceResult}
        />
      </ModalBase>
    </Container>
  );
};

export default RaceCard;
